import { Provider, Type } from '@angular/core';
import { TestBed, TestBedStatic } from '@angular/core/testing';

import { BaseSpecOverrides } from '../base/options';
import { isType, doesServiceImplementsOnDestroy } from '../types';

import { initialServiceModule } from './initial-module';
import { getDefaultServiceOptions, SpecServiceOptions } from './options';
import { SpecService } from './spec-service';
import { overrideModules } from '../Spec/create-factory';

/**
 * @publicApi
 */
export type SpecServiceFactory<S> = (overrides?: SpecServiceOverrides<S>) => SpecService<S>;

/**
 * @publicApi
 */
// tslint:disable-next-line:no-empty-interface
export interface SpecServiceOverrides<S> extends BaseSpecOverrides {}

/**
 * @publicApi
 */
export function createServiceFactory<S>(typeOrOptions: Type<S> | SpecServiceOptions<S>): SpecServiceFactory<S> {
  const service = isType(typeOrOptions) ? typeOrOptions : typeOrOptions.service;
  const options = isType(typeOrOptions) ? getDefaultServiceOptions<S>({ service }) : getDefaultServiceOptions(typeOrOptions);

  const moduleMetadata = initialServiceModule(options);

  beforeEach(() => {
    TestBed.configureTestingModule(moduleMetadata);
    overrideModules(options);
  });

  afterEach(() => {
    const testedService = (<any>TestBed).inject
      ? (<{ inject<T>(token: Type<T>, notFoundValue?: T): T } & TestBedStatic>TestBed).inject<S>(service)
      : TestBed.get(service);

    if (doesServiceImplementsOnDestroy(testedService)) {
      // tslint:disable-next-line:no-life-cycle-call
      testedService.ngOnDestroy();
    }
  });

  return (overrides?: SpecServiceOverrides<S>) => {
    const defaults: SpecServiceOverrides<S> = { providers: [] };
    const { providers } = { ...defaults, ...overrides };

    if (providers && providers.length) {
      providers.forEach((provider: Provider) => {
        TestBed.overrideProvider((provider as any).provide, provider as any);
      });
    }

    return new SpecService<S>(TestBed.inject ? TestBed.inject(service) : TestBed.get(service));
  };
}
